import type { RagWorkspace } from "../types.js";
import {
  canDeleteWorkspace,
  canManageWorkspaceMembers,
  canManageWorkspaceSettings,
} from "./workspace-permissions.js";

export type KmPermissionChecker = (resource: string, action: string) => boolean;

function isOrgAdminRole(orgRole: string | null | undefined): boolean {
  return orgRole === "owner" || orgRole === "admin" || orgRole === "manager";
}

export function canCreateKmWorkspace(hasPermission: KmPermissionChecker): boolean {
  return hasPermission("kmWorkspace", "create");
}

export function canUpdateKmWorkspace(
  hasPermission: KmPermissionChecker,
  userId: string | undefined,
  workspace: Pick<RagWorkspace, "ownerId" | "members">,
): boolean {
  return hasPermission("kmWorkspace", "update") && canManageWorkspaceSettings(userId, workspace);
}

export function canDeleteKmWorkspace(
  hasPermission: KmPermissionChecker,
  userId: string | undefined,
  workspace: Pick<RagWorkspace, "ownerId" | "isDefault" | "kind">,
  orgRole?: string | null,
): boolean {
  if (workspace.kind === "ORGANIZATION") {
    if (workspace.isDefault) {
      return false;
    }
    return isOrgAdminRole(orgRole) || hasPermission("kmWorkspace", "delete");
  }

  if (workspace.isDefault) {
    if (!hasPermission("kmWorkspace", "deleteDefault")) {
      return false;
    }
    return canDeleteWorkspace(userId, workspace, true);
  }

  if (!hasPermission("kmWorkspace", "delete")) {
    return false;
  }

  return canDeleteWorkspace(userId, workspace);
}

export function canManageKmWorkspaceMembers(
  hasPermission: KmPermissionChecker,
  userId: string | undefined,
  workspace: Pick<RagWorkspace, "ownerId" | "isDefault" | "members">,
): boolean {
  if (!canManageWorkspaceMembers(userId, workspace)) {
    return false;
  }

  if (userId && workspace.ownerId === userId) {
    return true;
  }

  return hasPermission("kmWorkspace", "manageMembers");
}

export function canCreateKmLibrary(
  hasPermission: KmPermissionChecker,
  userId: string | undefined,
  workspace: Pick<RagWorkspace, "ownerId">,
): boolean {
  if (!hasPermission("kmLibrary", "create")) {
    return false;
  }

  return Boolean(userId && workspace.ownerId === userId);
}

export function canUpdateKmLibrary(
  hasPermission: KmPermissionChecker,
  userId: string | undefined,
  workspace: Pick<RagWorkspace, "ownerId" | "members">,
): boolean {
  return hasPermission("kmLibrary", "update") && canManageWorkspaceSettings(userId, workspace);
}

export function canDeleteKmLibrary(
  hasPermission: KmPermissionChecker,
  userId: string | undefined,
  workspace: Pick<RagWorkspace, "ownerId" | "members">,
): boolean {
  return hasPermission("kmLibrary", "delete") && canManageWorkspaceSettings(userId, workspace);
}

export function canUploadKmDocument(hasPermission: KmPermissionChecker): boolean {
  return hasPermission("kmDocument", "upload");
}

export function canViewKmDocument(hasPermission: KmPermissionChecker): boolean {
  return hasPermission("kmDocument", "read");
}

export function canRegenerateKmDocument(hasPermission: KmPermissionChecker): boolean {
  return hasPermission("kmDocument", "regenerate");
}

export function canDeleteKmDocument(hasPermission: KmPermissionChecker): boolean {
  return hasPermission("kmDocument", "delete");
}
